// ZPL (Zebra Programming Language) generator for the 4"\u00d76" box labels
// printed on the Zebra ZD421 (203 dpi).
//
// Coordinates are in dots. At 203 dpi:
//   4"  = 812 dots (width, PW)
//   6"  = 1218 dots (length, LL)
// Safe margin ~24 dots each side. All fonts use built-in \u201c^A0\u201d scalable font.

const PW = 812;   // print width in dots
const LL = 1218;  // label length in dots
const M = 30;     // left/right safe margin in dots
const CONTENT_W = PW - M * 2;

const kg = (g) => `${((g || 0) / 1000).toFixed(2)} kg`;

// Escape any characters that would be interpreted as ZPL control chars.
// ~ and ^ are the two format-command prefixes; strip them defensively.
const clean = (s) => String(s ?? '').replace(/[\^~]/g, ' ').trim();

// Format a single label as ZPL.
export const buildLabelZpl = ({ plan, carton, index, total, printedAt }) => {
  const src = plan.source || {};
  let title = 'Packing list';
  let sub = 'Manual pack';
  if (src.type === 'order') {
    title = src.orderNumber || src.id || 'Order';
    sub = [src.customerName, src.customerCode].filter(Boolean).join(' \u00b7 ') || '';
  } else if (src.type === 'saver') {
    title = src.saverName || `Saver ${src.saverNumber || ''}`.trim();
    sub = [src.customerName, src.customerCode].filter(Boolean).join(' \u00b7 ') || '';
  }
  title = clean(title);
  sub = clean(sub);

  const totalTubs = carton.contents.reduce((s, c) => s + c.qty, 0);
  const weightLine =
    total > 1
      ? `${totalTubs} tubs \u00b7 ${kg(carton.totalWeightG)} \u00b7 ship ${kg(plan.totalShipmentWeightG)}`
      : `${totalTubs} tubs \u00b7 ${kg(carton.totalWeightG)}`;

  // Vertical layout budget.
  //   y = 30 .. 190   title + subtitle
  //   y = 210 divider
  //   y = 240 .. 470  Box N of M
  //   y = 490 carton size
  //   y = 540 contents header
  //   y = 580 .. 1050 contents rows
  //   y = 1080 divider
  //   y = 1100 footer (weight + date)
  const rows = carton.contents.slice(0, 12); // safety cap
  const rowHeightDots = Math.min(46, Math.floor((1050 - 580) / Math.max(rows.length, 1)));
  const contentRows = rows.map((c, i) => {
    const y = 580 + i * rowHeightDots;
    const sku = clean(c.sku);
    return [
      `^FO${M},${y}^A0N,34,34^FD${sku}^FS`,
      `^FO${M},${y}^A0N,34,34^FB${CONTENT_W},1,0,R,0^FDx ${c.qty}^FS`,
    ].join('\n');
  }).join('\n');

  const zpl = [
    '^XA',
    '^CI28',                       // UTF-8
    `^PW${PW}`,
    `^LL${LL}`,
    '^LH0,0',
    '^LT0',

    // Title (up to 2 lines, auto-wrap in the field block).
    `^FO${M},30^A0N,60,60^FB${CONTENT_W},2,0,L,0^FD${title}^FS`,
    // Subtitle
    `^FO${M},160^A0N,30,30^FB${CONTENT_W},1,0,L,0^FD${sub}^FS`,
    // Divider
    `^FO${M},210^GB${CONTENT_W},3,3^FS`,

    // Big "Box N of M"
    `^FO${M},250^A0N,140,140^FB${CONTENT_W},1,0,C,0^FDBox ${index + 1}^FS`,
    `^FO${M},400^A0N,55,55^FB${CONTENT_W},1,0,C,0^FDof ${total}^FS`,

    // Carton size line
    `^FO${M},480^A0N,26,26^FB${CONTENT_W},1,0,C,0^FD${clean(carton.cartonName)}  ${carton.lengthMm} x ${carton.widthMm} x ${carton.heightMm} mm^FS`,

    // Contents header + underline
    `^FO${M},540^A0N,26,26^FDSKU^FS`,
    `^FO${M},540^A0N,26,26^FB${CONTENT_W},1,0,R,0^FDQTY^FS`,
    `^FO${M},572^GB${CONTENT_W},2,2^FS`,

    // Contents rows
    contentRows,

    // Footer divider + footer line
    `^FO${M},1080^GB${CONTENT_W},3,3^FS`,
    `^FO${M},1100^A0N,30,30^FD${weightLine}^FS`,
    `^FO${M},1100^A0N,30,30^FB${CONTENT_W},1,0,R,0^FD${clean(printedAt)}^FS`,

    '^PQ1,0,1,Y',                 // print quantity 1, no pause, cut off
    '^XZ',
  ].join('\n');

  return zpl;
};

// Build ZPL for every carton in a plan.
export const buildPlanZpl = (plan, printedAtLabel) => {
  const printedAt = printedAtLabel || new Date().toLocaleDateString('en-NZ');
  return plan.cartons.map((carton, i) =>
    buildLabelZpl({
      plan,
      carton,
      index: i,
      total: plan.cartons.length,
      printedAt,
    })
  );
};
